import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  TENANT_SITE_IMAGE_DEFAULT_MAX_SIZE_BYTES,
  TenantSiteImageAssetConfig,
  TenantSiteImageStorageService,
} from '../../services/TenantSiteImageStorageService';
import type { TenantSiteAsset, TenantSitePageKey } from '../../types/site-pages';

interface TenantSiteImageUploadFieldProps {
  asset?: TenantSiteAsset | null;
  assetConfig?: TenantSiteImageAssetConfig;
  assetPath: string;
  clientId: string;
  label: string;
  onClear?: () => void;
  onUploaded: (asset: TenantSiteAsset) => void;
  pageKey: TenantSitePageKey;
}

const TenantSiteImageUploadField = ({
  asset,
  assetConfig,
  assetPath,
  clientId,
  label,
  onClear,
  onUploaded,
  pageKey,
}: TenantSiteImageUploadFieldProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { user, selectedClient } = useAuth();
  const maxSizeBytes = assetConfig?.maxSizeBytes ?? TENANT_SITE_IMAGE_DEFAULT_MAX_SIZE_BYTES;
  const maxSizeLabel = `${(maxSizeBytes / (1024 * 1024)).toFixed(maxSizeBytes % (1024 * 1024) === 0 ? 0 : 1)}MB`;

  const handleSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!user) {
      toast.error('Você precisa estar autenticado para enviar imagens.');
      return;
    }

    try {
      setIsUploading(true);
      const uploadedAsset = await TenantSiteImageStorageService.uploadImage({
        assetConfig,
        clientId,
        pageKey,
        assetPath,
        file,
        user,
        selectedClient,
      });

      if (asset?.storagePath && asset.provider === 'firebase-storage') {
        try {
          await TenantSiteImageStorageService.deleteImage(asset, user, selectedClient);
        } catch (cleanupError) {
          console.warn('Não foi possível remover a imagem anterior após substituição:', cleanupError);
        }
      }

      onUploaded({
        ...uploadedAsset,
        alt: asset?.alt ?? uploadedAsset.alt ?? '',
      });

      toast.success('Imagem enviada com sucesso.');
    } catch (error) {
      console.error('Erro ao enviar imagem institucional:', error);
      toast.error('Não foi possível enviar a imagem.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClear = async () => {
    if (!asset) {
      onClear?.();
      return;
    }

    try {
      setIsUploading(true);

      if (user && asset.storagePath && asset.provider === 'firebase-storage') {
        await TenantSiteImageStorageService.deleteImage(asset, user, selectedClient);
      }

      onClear?.();
      toast.success('Imagem removida com sucesso.');
    } catch (error) {
      console.error('Erro ao remover imagem institucional:', error);
      toast.error('Não foi possível remover a imagem.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => void handleSelectFile(event)}
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium text-slate-800">{label}</p>
            <p className="text-xs text-slate-500">
              Upload em Firebase Storage com isolamento por tenant.
              {assetConfig?.recommendedDimensions ? ` Dimensão sugerida: ${assetConfig.recommendedDimensions}.` : ''}
              {` Limite otimizado: ${maxSizeLabel}.`}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {isUploading ? 'Enviando...' : 'Enviar imagem'}
            </button>

            {onClear && (asset?.downloadUrl || asset?.storagePath) ? (
              <button
                type="button"
                onClick={() => void handleClear()}
                disabled={isUploading}
                className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />
                Limpar
              </button>
            ) : null}
          </div>
        </div>

        {asset?.downloadUrl ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            <img
              src={asset.downloadUrl}
              alt={asset.alt || label}
              className="h-48 w-full object-cover object-center"
            />
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400">
            Nenhuma imagem enviada
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantSiteImageUploadField;
